import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SuperHeader } from '@shared/components/SuperHeader/SuperHeader';
import { SuperSidebar } from '@shared/components/SuperSidebar/SuperSidebar';
import { SuperBottomSheet } from '@shared/components/SuperBottomSheet/SuperBottomSheet';
import { SuperIcon } from '@shared/components/SuperIcon/SuperIcon';
import { SuperAvatar } from '@shared/components/SuperAvatar/SuperAvatar';
import { useT } from '@shared/i18n/useT';
import { useAuthStore } from '@features/auth/store/auth.store';
import { useThemeStore } from '@app/theme/theme.store';
import { useIsMobile } from '@shared/hooks/useBreakpoint';
import type {
  SidebarSection,
  SidebarSectionItem,
} from '@shared/components/SuperSidebar/SuperSidebar.types';

function Brand() {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: '50%',
          border: '1px solid var(--color-text-mute)',
          fontFamily: 'var(--font-serif)',
          fontStyle: 'italic',
          fontSize: 13,
          color: 'var(--color-text)',
        }}
      >
        G
      </span>
      <span
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 18,
          letterSpacing: '-0.01em',
          color: 'var(--color-text)',
        }}
      >
        Greta
        <span style={{ fontStyle: 'italic', color: 'var(--color-text-dim)' }}>Bot</span>
        <span
          style={{
            marginLeft: 6,
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--color-accent)',
          }}
        >
          ADMIN
        </span>
      </span>
    </div>
  );
}

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT('common');
  const isMobile = useIsMobile();
  const user = useAuthStore((s) => s.user);
  const themeMode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const [moreOpen, setMoreOpen] = useState(false);

  const sections: ReadonlyArray<SidebarSection> = [
    {
      title: t('navAdmin'),
      items: [
        { id: 'dashboard', label: t('navDashboard'), icon: 'dashboard', to: '/dashboard' },
        { id: 'workspaces', label: t('navWorkspaces'), icon: 'users', to: '/workspaces' },
        { id: 'plans', label: t('navPlans'), icon: 'star', to: '/plans' },
        { id: 'free-codes', label: t('navFreeCodes'), icon: 'tag', to: '/free-codes' },
        { id: 'tokens', label: t('navTokenUsage'), icon: 'coin', to: '/tokens' },
        { id: 'audit', label: t('navAudit'), icon: 'note', to: '/audit' },
      ],
    },
    {
      title: t('navSystem'),
      items: [{ id: 'settings', label: t('navSettings'), icon: 'settings', to: '/settings' }],
    },
  ];

  const mobileShortcuts: ReadonlyArray<SidebarSectionItem> = [
    { id: 'dashboard', label: t('navDashboard'), icon: 'dashboard', to: '/dashboard' },
    { id: 'workspaces', label: t('navWorkspaces'), icon: 'users', to: '/workspaces' },
    { id: 'free-codes', label: t('navFreeCodes'), icon: 'tag', to: '/free-codes' },
    { id: 'audit', label: t('navAudit'), icon: 'note', to: '/audit' },
  ];

  const allItems = sections.flatMap((s) => s.items);

  return (
    <div className="gb-ambient" style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          minHeight: '100vh',
          paddingBottom: isMobile ? 64 : 0,
        }}
      >
        {!isMobile && (
          <SuperSidebar
            sections={sections}
            activePath={location.pathname}
            brand={<Brand />}
            footer={
              user && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    paddingTop: 12,
                    borderTop: '1px solid var(--color-border-soft)',
                  }}
                >
                  <SuperAvatar name={user.name} size="sm" />
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 12,
                        color: 'var(--color-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {user.name}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 9,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: 'var(--color-text-mute)',
                      }}
                    >
                      SUPERADMIN
                    </span>
                  </span>
                </div>
              )
            }
          />
        )}

        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <SuperHeader
            leading={isMobile ? <Brand /> : undefined}
            trailing={
              <>
                <button
                  type="button"
                  aria-label={
                    themeMode === 'dark' ? t('themeToggleToLight') : t('themeToggleToDark')
                  }
                  onClick={toggleTheme}
                  style={{
                    background: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    width: 32,
                    height: 32,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-dim)',
                    cursor: 'pointer',
                  }}
                >
                  <SuperIcon name={themeMode === 'dark' ? 'sun' : 'moon'} size="md" />
                </button>
                <button
                  type="button"
                  aria-label="Notifications"
                  style={{
                    background: 'transparent',
                    border: '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    width: 32,
                    height: 32,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-dim)',
                    cursor: 'pointer',
                  }}
                >
                  <SuperIcon name="bell" size="md" />
                </button>
                {user && <SuperAvatar name={user.name} size="sm" />}
              </>
            }
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <Outlet />
          </div>
        </main>

        {isMobile && (
          <SuperSidebar
            sections={sections}
            activePath={location.pathname}
            mobileShortcuts={mobileShortcuts}
            onMobileMore={() => setMoreOpen(true)}
          />
        )}
      </div>

      <SuperBottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={t('navMore')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {allItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setMoreOpen(false);
                navigate(item.to);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: 12,
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text)',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: 14,
              }}
            >
              <SuperIcon name={item.icon} size="md" />
              {item.label}
            </button>
          ))}
        </div>
      </SuperBottomSheet>
    </div>
  );
}
